// Transcripts list View (transcripts.md): applied-filter chip row + the
// first `virtual.fill` adopter — fixed chrome, the virtualized table body
// owns the scroll at remaining viewport height (UI-LAW §4b.3). Renders a
// fragment so its children join `Page variant="workspace"`'s flex column
// (a Stack wrapper would break the fill-height chain). Props-pure.

import { useMemo, type ReactNode } from "react";

import type { components } from "../../api/schema";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { transcriptTableColumns } from "./transcriptTableColumns";

type TranscriptListItem = components["schemas"]["TranscriptListItem"];

export interface TranscriptsListProps {
  // Resolves the team-wide Agent column's ids to display names (only consulted
  // when `showAgentColumn` is true).
  agentNamesById?: ReadonlyMap<string, string>;
  // The `?batch=` BatchRollupStrip, rendered above the table.
  batchStrip?: ReactNode;
  emptyState: ReactNode;
  errorMessage?: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: TranscriptListItem[];
  listStateKey: string;
  onLoadMore: () => void;
  // Whole-row click opens the transcript's detail route.
  onOpenTranscript: (id: string) => void;
  // True on the team-wide (unscoped) list — shows the Agent column.
  showAgentColumn: boolean;
}

export function TranscriptsList({
  agentNamesById,
  batchStrip,
  emptyState,
  errorMessage,
  hasNextPage,
  isFetchingNextPage,
  isPending,
  items,
  listStateKey,
  onLoadMore,
  onOpenTranscript,
  showAgentColumn,
}: TranscriptsListProps) {
  const columns = useMemo(
    () =>
      transcriptTableColumns({
        agentNamesById,
        showAgent: showAgentColumn,
      }),
    [agentNamesById, showAgentColumn],
  );

  return (
    <>
      {batchStrip}
      <PaginatedTableSection
        ariaLabel="Transcripts"
        columns={columns}
        emptyState={emptyState}
        enableSorting
        errorMessage={errorMessage}
        getRowId={(transcript) => transcript.id}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isPending={isPending}
        items={items}
        listStateKey={listStateKey}
        skeletonLabel="Loading transcripts"
        skeletonRows={8}
        virtual={{ fill: true }}
        onLoadMore={onLoadMore}
        onRowClick={(row) => {
          onOpenTranscript(row.original.id);
        }}
      />
    </>
  );
}
